"use client"

import { useActionState } from "react"
import Link from "next/link"
import { LockKeyhole, Mail, ShieldCheck } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import { loginAction, type LoginState } from "./actions"

const initialState: LoginState = { error: null }

export default function LoginPage() {
  const [state, formAction, pending] = useActionState(loginAction, initialState)

  return (
    <div className="relative rounded-3xl border border-[var(--border-default)] bg-bg-surface/70 p-8 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl">
      {/* Top gradient hairline */}
      <div
        aria-hidden
        className="absolute inset-x-8 top-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, var(--accent-prime), transparent)",
        }}
      />

      {/* Brand */}
      <div className="mb-8 flex flex-col items-center text-center">
        <div className="display text-4xl font-bold tracking-tight">
          ENNEX<span className="text-prime">ONE</span>
        </div>
        <div className="mono-label mt-2 text-[10px]">
          POWER · PRECISION · PRODUCTION
        </div>
      </div>

      {/* Form */}
      <form action={formAction} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email" className="mono-label text-[10px]">
            EMAIL
          </Label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="you@ennex.local"
              autoComplete="email"
              required
              className="bg-bg-elevated pl-9"
            />
          </div>
          {state.fieldErrors?.email && (
            <p className="text-xs text-alert">{state.fieldErrors.email[0]}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="mono-label text-[10px]">
            PASSWORD
          </Label>
          <div className="relative">
            <LockKeyhole className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <Input
              id="password"
              name="password"
              type="password"
              placeholder="••••••••"
              autoComplete="current-password"
              required
              className="bg-bg-elevated pl-9"
            />
          </div>
          {state.fieldErrors?.password && (
            <p className="text-xs text-alert">{state.fieldErrors.password[0]}</p>
          )}
        </div>

        {state.error && (
          <div className="rounded-md border border-alert/30 bg-alert/10 px-3 py-2 text-sm text-alert">
            {state.error}
          </div>
        )}

        <p className="flex items-center justify-center gap-1.5 text-[10px] text-text-dim">
          <ShieldCheck className="h-3 w-3 text-prime" />
          หลังกรอกรหัสผ่าน ระบบจะส่งรหัส OTP ไปยังอีเมลของคุณเพื่อยืนยันตัวตน
        </p>

        <Button
          type="submit"
          disabled={pending}
          className="group relative w-full overflow-hidden bg-prime text-bg-void font-mono uppercase tracking-wider hover:bg-prime-soft"
        >
          <span className="relative z-10 inline-flex items-center gap-2">
            {pending ? (
              "กำลังส่งรหัส OTP..."
            ) : (
              <>
                <ShieldCheck className="h-4 w-4" />
                ดำเนินการต่อ · รับ OTP
              </>
            )}
          </span>
          {/* Shine sweep on hover */}
          <span
            aria-hidden
            className="absolute inset-y-0 -left-full w-1/3 -skew-x-12 bg-white/30 transition-all duration-500 group-hover:left-full"
          />
        </Button>
      </form>

      {/* Demo creds */}
      <div className="mt-6 rounded-lg border border-[var(--border-subtle)] bg-bg-elevated/60 p-3 text-center">
        <div className="mono-label mb-1 text-[9px]">DEMO</div>
        <div className="font-mono text-xs text-text-bright">
          admin@ennex.local · <span className="text-prime">ennex1234</span>
        </div>
        <div className="mt-0.5 text-[10px] text-text-muted">
          (7 roles available · password เหมือนกัน)
        </div>
      </div>

      <p className="mt-4 text-center text-[10px] text-text-dim">
        <Link href="/" className="hover:text-prime">
          ← back to home
        </Link>
        <span className="mx-2">·</span>
        <span>Powered by Momo Infinitech</span>
      </p>

      {/* Bottom gradient hairline */}
      <div
        aria-hidden
        className="absolute inset-x-8 bottom-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, var(--accent-purple), transparent)",
        }}
      />
    </div>
  )
}
